<template>
  <div class="coin-transactions-list flex flex-col">
    <!-- loading 狀態 -->
    <div v-if="state.initialLoading" class="flex justify-center items-center py-8">
      <p class="text-16px font-medium text-center">
        <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </p>
    </div>

    <!-- error 狀態 -->
    <div v-else-if="state.error" class="flex flex-col items-center justify-center py-8">
      <p class="text-14px text-red-300 text-center">
        {{ $t('private_profiles.transaction_table.load_error') }}
      </p>
    </div>

    <!-- 無資料狀態 -->
    <div v-else-if="data.items.length === 0" class="flex flex-col items-center justify-center py-8">
      <p class="text-14px text-gray-200 text-center">
        {{ $t('private_profiles.transaction_table.empty') }}
      </p>
    </div>

    <!-- 有資料時顯示內容 -->
    <div v-else class="flex flex-col">
      <!-- Desktop header -->
      <div class="hidden sm:grid grid-cols-12 text-grey-3 text-12px font-medium py-2 border-b border-gray-500">
        <div class="col-span-3 pl-2">{{ $t('private_profiles.transaction_table.created_at') }}</div>
        <div class="col-span-7">{{ $t('private_profiles.transaction_table.activity') }}</div>
        <div class="col-span-2 text-right pr-2">{{ $t('private_profiles.transaction_table.amount') }}</div>
      </div>

      <!-- Rows -->
      <div
        v-for="tx in data.items"
        :key="tx.id"
        class="grid grid-cols-1 sm:grid-cols-12 py-3 items-center bg-gray-600 mt-2 rounded-lg text-14px"
      >
        <div class="sm:col-span-3 pl-2">{{ formatDate(tx.created_at) }}</div>
        <div class="sm:col-span-7 pl-2 sm:pl-0">
          <span>{{ tx.activity.prefix }}</span><!--
       --><a
            v-if="tx.activity.ref_label && tx.activity.ref_url"
            :href="tx.activity.ref_url"
            class="text-orange-300 underline"
          >{{ tx.activity.ref_label }}</a><!--
       --><span v-else-if="tx.activity.ref_label">{{ tx.activity.ref_label }}</span><!--
       --><span>{{ tx.activity.suffix }}</span>
        </div>
        <div class="sm:col-span-2 pl-2 sm:pl-0 sm:text-right sm:pr-2">
          <span
            class="inline-block rounded-full border px-3 py-0.5 text-12px leading-5 font-medium"
            :class="tx.earn ? 'text-green-300 border-green-300' : 'text-red-300 border-red-300'"
          >
            {{ formatAmount(tx) }}
          </span>
        </div>
      </div>

      <!-- loading more -->
      <div v-if="state.loading" class="py-4 flex justify-center">
        <img src="../../images/icons/loading.png" class="w-6 h-6 icon loading-icon"/>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, onMounted, onUnmounted } from 'vue'
import debounce from 'lodash/debounce'

export default defineComponent({
  name: 'CoinTransactionsList',

  setup() {
    const state = reactive({
      loading: false,
      initialLoading: true,
      error: false,
      perPage: 10
    })

    const data = reactive({
      items: [],
      totalCount: 0,
      page: 0,
      hasMore: true
    })

    let scrollHandler = null

    const fetchPage = async (page = 1) => {
      if (!data.hasMore || state.loading) return
      state.loading = true
      try {
        const params = new URLSearchParams({ page, per_page: state.perPage })
        const response = await fetch(`/api/v1/coin_transactions?${params.toString()}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const body = await response.json()
        const items = body.data || []
        items.forEach(item => {
          if (!data.items.some(existing => existing.id === item.id)) {
            data.items.push(item)
          }
        })
        if (body.pagination) {
          data.page = body.pagination.current_page
          data.totalCount = body.pagination.total_count
          data.hasMore = body.pagination.current_page < body.pagination.total_pages
        }
      } catch (err) {
        console.error('Failed to load coin transactions:', err)
        state.error = true
        data.hasMore = false
      } finally {
        state.loading = false
        state.initialLoading = false
      }
    }

    const onScroll = debounce(() => {
      if (!data.hasMore || state.loading) return
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const docHeight = document.documentElement.scrollHeight
      const percent = (scrollTop / (docHeight - windowHeight)) * 100
      if (percent > 70) fetchPage(data.page + 1)
    }, 100)

    const formatDate = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
    }

    const formatAmount = (tx) => {
      const value = Number(tx.amount)
      if (Number.isNaN(value)) return tx.amount
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}`
    }

    onMounted(() => {
      fetchPage(1)
      scrollHandler = onScroll
      window.addEventListener('scroll', scrollHandler, { passive: true })
    })

    onUnmounted(() => {
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler)
    })

    return { state, data, formatDate, formatAmount }
  }
})
</script>
